/**
 * VehicleLocationDialog Component
 *
 * Dialog for manually updating vehicle location.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  DialogContent,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { ParkingSpotSelector } from './ParkingSpotSelector';
import {
  useUpdateVehicleLocation,
  type VehicleLocationType,
} from '@/hooks/useWorkOrders';
import { useAllLifts } from '@/hooks/useLifts';
import { useNotify } from '@/stores/notificationStore';

interface VehicleLocationDialogProps {
  open: boolean;
  workOrderId: string;
  onClose: () => void;
}

const LOCATION_TYPES: VehicleLocationType[] = [
  'on_lift',
  'parking',
];

export function VehicleLocationDialog({
  open,
  workOrderId,
  onClose,
}: VehicleLocationDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateLocation = useUpdateVehicleLocation();
  const { data: lifts } = useAllLifts();

  const [locationType, setLocationType] = useState<VehicleLocationType | ''>('');
  const [selectedLiftId, setSelectedLiftId] = useState('');
  const [parkingZoneId, setParkingZoneId] = useState('');
  const [parkingSpotId, setParkingSpotId] = useState('');

  const canSubmit =
    locationType === 'on_lift'
      ? selectedLiftId !== ''
      : locationType === 'parking'
        ? parkingSpotId !== '' && parkingZoneId !== ''
        : false;

  const handleSubmit = async () => {
    if (!locationType || !canSubmit) return;
    try {
      await updateLocation.mutateAsync({
        id: workOrderId,
        data: {
          locationType,
          ...(locationType === 'on_lift' ? { locationLiftId: selectedLiftId } : {}),
          ...(locationType === 'parking'
            ? { locationZoneId: parkingZoneId, locationSpotId: parkingSpotId }
            : {}),
        },
      });
      notify.success(t('vehicleLocation.locationUpdated'));
      onClose();
      // Reset
      setLocationType('');
      setSelectedLiftId('');
      setParkingZoneId('');
      setParkingSpotId('');
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('vehicleLocation.updateFailed') }));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('vehicleLocation.changeLocation')}
      isDirty={!!locationType}
      onSubmit={handleSubmit}
      isLoading={updateLocation.isPending}
      submitDisabled={!canSubmit}
      submitLabel={tc('save')}
      maxWidth={locationType === 'parking' ? 'md' : 'sm'}
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            select
            label={t('vehicleLocation.selectType')}
            value={locationType}
            onChange={(e) => {
              setLocationType(e.target.value as VehicleLocationType);
              setSelectedLiftId('');
              setParkingZoneId('');
              setParkingSpotId('');
            }}
            fullWidth
          >
            <MenuItem value="">
              <em>-</em>
            </MenuItem>
            {LOCATION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`vehicleLocation.${type}`)}
              </MenuItem>
            ))}
          </TextField>

          {locationType === 'on_lift' && (
            <TextField
              select
              label={t('vehicleLocation.selectLift')}
              value={selectedLiftId}
              onChange={(e) => setSelectedLiftId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                <em>-</em>
              </MenuItem>
              {(lifts ?? []).map((lift) => (
                <MenuItem key={lift.id} value={lift.id}>
                  {lift.name} ({lift.identifier})
                </MenuItem>
              ))}
            </TextField>
          )}

          {locationType === 'parking' && (
            <ParkingSpotSelector
              selectedZoneId={parkingZoneId}
              selectedSpotId={parkingSpotId}
              onZoneChange={setParkingZoneId}
              onSpotChange={setParkingSpotId}
            />
          )}
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
